/**
 * SendToYouTubeDialog — one-click apply of a generated channel kit
 * to the user's real YouTube channel via OAuth + channels.update.
 *
 * Two states:
 *   1. NOT LINKED — shows a "Connect YouTube" button that opens the
 *      Google consent screen in a new tab. When they finish, the
 *      OAuth callback redirects back to /create-channel with a
 *      `?yt_link=success` query flag; the parent page polls status
 *      and rerenders this dialog into the "linked" state.
 *   2. LINKED — shows detected channel name, banner picker (A/B/none),
 *      optional "also overwrite channel title" toggle, Apply button.
 *
 * On apply the button turns into a progress toast — each field
 * uploads sequentially server-side (banner takes the longest),
 * total ~15-30s. On success we close the modal and let the parent
 * refetch the kit so the card can show the applied badge.
 *
 * Avatar limitation: YouTube's public API does NOT expose channel-
 * avatar upload, so we surface a "download avatar.png separately"
 * hint at the bottom of the linked state.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  applyKitToYouTube,
  getYouTubeLinkStatus,
  startYouTubeLink,
  unlinkYouTube,
} from "@/lib/api";

export default function SendToYouTubeDialog({ kit, onClose, onApplied }) {
  const [status, setStatus] = useState(null); // { linked, configured, ... }
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [bannerChoice, setBannerChoice] = useState("banner_1");
  const [overwriteTitle, setOverwriteTitle] = useState(false);

  const refresh = async () => {
    try {
      const s = await getYouTubeLinkStatus();
      setStatus(s);
    } catch (err) {
      setStatus({ linked: false, configured: false, reason: err?.message || "Status check failed" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // Poll every 3s while modal is open — catches the case where
    // the user finishes OAuth in a new tab and we need to auto-flip
    // this dialog into the "linked" state.
    const iv = setInterval(refresh, 3000);
    return () => clearInterval(iv);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleConnect = async () => {
    try {
      const url = await startYouTubeLink();
      window.open(url, "_blank", "noopener,noreferrer");
      toast.info("Complete the Google consent flow in the new tab — this dialog will refresh when you're done.");
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Couldn't start YouTube connect flow",
      );
    }
  };

  const handleUnlink = async () => {
    if (!window.confirm("Unlink your YouTube channel from YT Maker?")) return;
    try {
      await unlinkYouTube();
      toast.success("YouTube unlinked");
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Unlink failed");
    }
  };

  const handleApply = async () => {
    setApplying(true);
    const toastId = toast.loading("Uploading to YouTube — this can take 20-30 seconds…");
    try {
      const summary = await applyKitToYouTube(kit.job_id, {
        banner_choice: bannerChoice,
        overwrite_title: overwriteTitle,
      });
      toast.success(
        `Applied to ${summary.channel_title_after || "your channel"}${
          summary.banner_uploaded ? " · banner uploaded" : ""
        }`,
        { id: toastId, duration: 6000 },
      );
      onApplied?.(summary);
      onClose?.();
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "YouTube apply failed — check the browser console",
        { id: toastId },
      );
    } finally {
      setApplying(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      data-testid="send-to-youtube-dialog"
    >
      <div className="bg-zinc-950 border border-zinc-800 rounded-sm max-w-lg w-full p-5 space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
              Send channel kit to YouTube
            </div>
            <div className="font-display text-lg font-bold text-white mt-1">
              {kit?.name || "channel kit"}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="send-to-yt-close"
            className="text-zinc-500 hover:text-white text-xl leading-none"
          >
            ×
          </button>
        </div>

        {loading && (
          <div className="text-[12px] text-zinc-500 font-mono">Checking YouTube link…</div>
        )}

        {!loading && status && !status.configured && (
          <div className="border border-red-500/40 bg-red-500/5 rounded-sm p-3">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-red-400 mb-1">
              Not configured
            </div>
            <div className="text-[12px] text-zinc-300 leading-snug">
              {status.reason || "Google OAuth credentials aren't set on the server."}
              {" "}Ask the server owner to configure{" "}
              <code className="text-yellow-400">GOOGLE_OAUTH_CLIENT_ID</code> and{" "}
              <code className="text-yellow-400">GOOGLE_OAUTH_CLIENT_SECRET</code>.
            </div>
          </div>
        )}

        {!loading && status?.configured && !status.linked && (
          <div className="space-y-3">
            <div className="text-[13px] text-zinc-300 leading-relaxed">
              YT Maker needs one-time permission to update your YouTube channel's
              description, keywords, and banner. Your channel avatar cannot be
              changed via API — you'll download it separately.
            </div>
            <button
              type="button"
              data-testid="send-to-yt-connect"
              onClick={handleConnect}
              className="w-full font-mono text-[11px] uppercase tracking-[0.2em] text-black bg-yellow-400 hover:bg-yellow-300 px-4 py-2 rounded-sm transition-colors"
            >
              ▸ Connect YouTube
            </button>
            <div className="text-[10px] text-zinc-600 font-mono">
              Opens Google consent in a new tab. This dialog refreshes automatically.
            </div>
          </div>
        )}

        {!loading && status?.linked && (
          <div className="space-y-4">
            <div className="border border-emerald-500/40 bg-emerald-500/5 rounded-sm p-3 flex items-center justify-between">
              <div>
                <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-emerald-400 mb-0.5">
                  ◉ Linked
                </div>
                <div className="text-[13px] font-bold text-white truncate">
                  {status.channel_title || "(no title)"}
                </div>
              </div>
              <button
                type="button"
                onClick={handleUnlink}
                data-testid="send-to-yt-unlink"
                className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500 hover:text-red-400"
              >
                Unlink
              </button>
            </div>

            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">
                Which banner to upload?
              </div>
              <div className="grid grid-cols-2 gap-2 mb-2">
                {["banner_1", "banner_2"].map((key) => {
                  const url = kit?.images?.[`${key}.png`] || null;
                  return (
                    <button
                      key={key}
                      type="button"
                      data-testid={`send-to-yt-banner-${key}`}
                      onClick={() => setBannerChoice(key)}
                      className={`relative border rounded-sm overflow-hidden transition-colors ${
                        bannerChoice === key
                          ? "border-yellow-400"
                          : "border-zinc-800 hover:border-zinc-600"
                      }`}
                    >
                      {url ? (
                        <img
                          src={url}
                          alt={key}
                          className="w-full aspect-video object-cover"
                        />
                      ) : (
                        <div className="w-full aspect-video bg-zinc-900" />
                      )}
                      <div className={`absolute bottom-0 left-0 right-0 px-2 py-1 font-mono text-[9px] uppercase tracking-[0.2em] ${
                        bannerChoice === key ? "bg-yellow-400 text-black" : "bg-black/70 text-zinc-300"
                      }`}>
                        {key === "banner_1" ? "Concept A" : "Concept B"}
                      </div>
                    </button>
                  );
                })}
              </div>
              <button
                type="button"
                data-testid="send-to-yt-banner-skip"
                onClick={() => setBannerChoice("")}
                className={`w-full font-mono text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 rounded-sm border transition-colors ${
                  bannerChoice === ""
                    ? "border-yellow-400 text-yellow-400 bg-yellow-400/10"
                    : "border-zinc-800 text-zinc-500 hover:border-zinc-600"
                }`}
              >
                Skip banner (only update text)
              </button>
            </div>

            <label className="flex items-center gap-2 cursor-pointer" data-testid="send-to-yt-overwrite-title-wrapper">
              <input
                type="checkbox"
                checked={overwriteTitle}
                onChange={(e) => setOverwriteTitle(e.target.checked)}
                data-testid="send-to-yt-overwrite-title"
                className="accent-yellow-400"
              />
              <span className="text-[12px] text-zinc-300">
                Also overwrite channel title with{" "}
                <span className="font-bold text-white">"{kit?.name}"</span>
              </span>
            </label>

            <div className="border-t border-zinc-800 pt-3 space-y-2">
              <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500">
                Will be updated:
              </div>
              <ul className="text-[12px] text-zinc-300 space-y-0.5 list-none">
                <li>✓ Channel description ({(kit?.description || "").length} chars)</li>
                <li>✓ Keywords ({(kit?.keywords || []).length} tags)</li>
                {bannerChoice && <li>✓ Banner image ({bannerChoice === "banner_1" ? "Concept A" : "Concept B"})</li>}
                {overwriteTitle && <li>✓ Channel title</li>}
                <li className="text-zinc-500">✗ Profile picture / avatar (upload manually in YouTube Studio — YouTube API limitation)</li>
              </ul>
            </div>

            <button
              type="button"
              data-testid="send-to-yt-apply"
              onClick={handleApply}
              disabled={applying}
              className={`w-full font-mono text-[11px] uppercase tracking-[0.2em] px-4 py-2 rounded-sm transition-colors ${
                applying
                  ? "bg-zinc-800 text-zinc-500 cursor-not-allowed"
                  : "bg-yellow-400 hover:bg-yellow-300 text-black"
              }`}
            >
              {applying ? "◌ Uploading…" : "▸ Apply to YouTube"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
